import { getAllData } from "apis/customApiHandler";
import ViewVehicleList from "components/vehicles/viewVehicleList";
import useDebounce from "hooks/useDebounce";
import ReactSelect from "react-select";
import { useState, useCallback } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Button, Input, Select } from "styled";
import { TH } from "styled/table";
import checkPermission from "utils/checkPermission";
import permissions from "utils/permissionConstants";
import exportExcel from "utils/exportExcel";
import { BiCloudDownload } from "react-icons/bi";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";

import AddVehicle from "components/vehicles/addVehicle";
import BulkUpload from "components/vehicles/bulkUpload";
import Spinner from "components/Spinner";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackSearchEvent, trackDownloadAndBulkUploadEvents, trackPageClickEvent } from "utils/analytics";
import { ANALYTICS_EVENTS } from "utils/analyticsEvents";

const Vehicles = () => {
  const query = window.location.search?.replace("?", "")?.split("&");
  const fleet = query[0]?.split("=")[1]?.replaceAll("%20", " ");
  const timeFrame = query[1]?.split("=")[1];
  const [vehicles, setVehicles] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedConfigurations, setSelectedConfigurations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [fleetMap, setFleetMap] = useState({});
  const [showAddmodel, setShowAddmodel] = useState(false);
  const [showBulkUploadModel, setShowBulkUploadModel] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [contractType, setContractType] = useState("");
  const [agreement, setAgreement] = useState("");
  const [downloading, setDownloading] = useState(false);
  const debouncedValue = useDebounce(search, 1000);


  const currentUser = useSelector((state) => state.user.currentUser);
  const location = useLocation();

  const getData = async (pg) => {
    setIsLoading(true);
    const dt = await getAllData("/vehicle", {
      searchQuery: debouncedValue,
      page: pg ? pg : page,
      limit: 100,
      fleet,
      ...(selectedFleets?.includes("all")
        ? {
            fleets: null,
          }
        : selectedFleets.length && {
            fleets: selectedFleets.map((fleet) => fleet).join(","),
          }),
      ...(selectedMakes?.includes("all")
        ? {
            makes: null,
            // getMakeOptions().filter(item => item.value!== "all" && item.value!== "clear")
            // .map((item) => item.value)
            //     .join(","),
          }
        : selectedMakes.length && {
            makes: selectedMakes.map((make) => make).join(","),
          }),
      ...(selectedModels?.includes("all")
        ? {
            models: null,
            // getModelOptions().filter(item => item.value!== "all" && item.value!== "clear")
            //   .map((item) => item.value)
            //       .join(","),
          }
        : selectedModels.length && {
            models: selectedModels.map((model) => model).join(","),
          }),
      ...(selectedConfigurations?.includes("all")
        ? {
            configurations: null,
            // getConfigurationOptions().filter(item => item.value!== "all" && item.value!== "clear")
            //   .map((item) => item.value)
            //       .join(","),
          }
        : selectedConfigurations.length && {
            configurations: selectedConfigurations
              .map((configuration) => configuration)
              .join(","),
          }),
      ...(contractType !== "" && {
        contractType,
      }),
      ...(agreement !== "" && {
        agreement,
      }),
      timeFrame,
    });
    setVehicles(dt);
    setIsLoading(false);
  };


  useEffect(() => {
    if (!selectedFleets.length && !search) {
      //console.log("")
    } else getData(page);
  }, [page]);

  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData("/customer-fleet", {
      limit: 300,
    })
  );
  const { data: allMakes } = useQuery([`/vehicle-make-master`], () =>
    getAllData("/vehicle-make-master", {
      limit: 300,
      page: 1,
    })
  );
  const { data: allModels } = useQuery([`/vehicle-model-master`], () =>
    getAllData("/vehicle-model-master", {
      limit: 300,
      page: 1,
    })
  );
  const { data: allConfigurations } = useQuery(
    [`/vehicle-configuration-master`],
    () =>
      getAllData("/vehicle-configuration-master", {
        limit: 2000,
        page: 1,
      })
  );


  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getMakeOptions = useCallback(() => {
    if (allMakes && allMakes.data && allMakes.data.length) {
      return [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...allMakes?.data?.map((mk) => {
          return {
            value: mk._id,
            label: mk.makeName,
          };
        }),
      ];
    }
  }, [allMakes]);

  const getModelOptions = useCallback(() => {
    if (allModels && allModels.data && allModels.data.length) {
      return [
        selectedMakes?.length && {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...(allModels?.data?.length &&
          allModels?.data
            ?.filter((model) => {
              if (selectedMakes.includes("all")) return true;
              const selectedMakesData = allMakes?.data?.filter((make) =>
                selectedMakes?.includes(make._id)
              );

              return selectedMakesData?.some((mk) => mk._id === model.make._id);
            })
            ?.map((model) => {
              return {
                value: model._id,
                label: model.name,
              };
            })),
      ];
    }
  }, [allMakes, allModels, selectedMakes]);

  const getConfigurationOptions = useCallback(() => {
    if (allConfigurations && allConfigurations?.data?.length) {
      const configs = [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...allConfigurations.data.map((configuration) => ({
          value: configuration._id,
          label: `${configuration.make?.makeName}/${configuration.model?.name}/${configuration.name}`,
        })),
      ];
      return configs;
    }
    return [];
  }, [allConfigurations]);

  const getDownloadData = async () => {
    setDownloading(true);
    trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.CLICK_DOWNLOAD_BUTTON, "Vehicles", location.pathname, { model_name: "Download Vehicles Data" });
    try {
      const dt = await getAllData("/vehicle/download", {
        searchQuery: debouncedValue,
        fleet,
        ...(selectedFleets?.includes("all")
          ? {
              fleets: getFleetOptions()
                .filter(
                  (item) => item.value !== "all" && item.value !== "clear"
                )
                .map((item) => item.value)
                .join(","),
            }
          : selectedFleets.length && {
              fleets: selectedFleets.map((fleet) => fleet).join(","),
            }),
        ...(selectedMakes?.includes("all")
          ? {
              makes: null,
              // getMakeOptions().filter(item => item.value!== "all" && item.value!== "clear")
              // .map((item) => item.value)
              //     .join(","),
            }
          : selectedMakes.length && {
              makes: selectedMakes.map((make) => make).join(","),
            }),
        ...(selectedModels?.includes("all")
          ? {
              models: null,
              // getModelOptions().filter(item => item.value!== "all" && item.value!== "clear")
              //   .map((item) => item.value)
              //       .join(","),
            }
          : selectedModels.length && {
              models: selectedModels.map((model) => model).join(","),
            }),
        ...(selectedConfigurations?.includes("all")
          ? {
              configurations: null,
              // getConfigurationOptions().filter(item => item.value!== "all" && item.value!== "clear")
              //   .map((item) => item.value)
              //       .join(","),
            }
          : selectedConfigurations.length && {
              configurations: selectedConfigurations
                .map((configuration) => configuration)
                .join(","),
            }),
        ...(contractType !== "" && {
          contractType,
        }),
        ...(agreement !== "" && {
          agreement,
        }),
        timeFrame,
      });
      // console.log(dt)
      exportExcel(
        dt?.data?.map((x, idx) => ({
          "S.N": idx + 1,
          "Vehicle Number": x.registrationNumber,
          Fleet: x?.fleet,
          Hub: x.hub,
          Make: x?.vehicleMake,
          Model: x?.model,
          Year: x?.registrationYear,
          Configuration: x?.configuration,
          "Engine No.": x?.engineNumber,
          "Chasis No": x?.chasisNumber,
          "Contract Type": x?.contractType?.name,
          "Mechanical Condition": x?.mechanicalCondition,
          "Body Type": x?.bodyType,
          "Application Type": x?.applicationType,
          // "Vehicle Type": x?.vehicleType?.name,
          "Vehicle CC": x?.vehicleCC,
          GVW: x?.GVW,
          "Fitment Date": x.fitmentDate
            ? x.fitmentDate
            : // new Date(x.fitmentDate).getDate() + "/" + (new Date(x.fitmentDate).getMonth() + 1) + "/" + new Date(x.fitmentDate).getFullYear()
              "",
          "Last Inspection Date": x.lastInspectionDate
            ? // new Date(x.lastInspectionDate).getDate() + "/" + (new Date(x.lastInspectionDate).getMonth() + 1) + "/" + new Date(x.lastInspectionDate).getFullYear()
              x.lastInspectionDate
            : "",
        })),
        "vehicles.xlsx"
      );
      trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.SYS_DOWNLOAD_SUCCESS, "Vehicles", location.pathname, { model_name: "Download Vehicles Data" });
      setDownloading(false);
    } catch (error) {
      trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.SYS_DOWNLOAD_ERROR, "Vehicles", location.pathname, { model_name: "Download Vehicles Data", errMsg: error?.message });
      setDownloading(false);
    }
  };

  const clickSearchBtn = () => {
    const params = {
      fleets: selectedFleets?.includes("all")
        ? "All"
        : selectedFleets?.map((id) => getFleetOptions()?.find((item) => item.value === id)?.label ?? id),
      configurations: selectedConfigurations?.includes("all")
        ? "All"
        : selectedConfigurations?.map((id) => getConfigurationOptions()?.find((item) => item.value === id)?.label ?? id),
      contract_type: contractType || "All",
      agreement: agreement || "All",
      search_value: search,
    };

    trackSearchEvent(ANALYTICS_EVENTS.CLICK_SEARCH_BUTTON, "Vehicles", location.pathname, params);
  };

  return (
    <div>
      <div className="m-10">
        <div className="mt-8 flex justify-start gap-6 items-center flex-wrap lg:flex-nowrap">
          <div className="w-full px-8 flex justify-center items-center gap-4">
            <label className="w-full py-3">
              Select Fleets
              <ReactSelect
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "transparent",
                    minWidth: "100%",
                    "&:hover": {
                      borderColor: "rgb(34 197 94)",
                    },
                  }),
                }}
                className="shadow-md rounded-md py-1 mb-2"
                options={getFleetOptions()}
                value={
                  selectedFleets &&
                  selectedFleets?.map((fleet) => {
                    return {
                      value: fleet,
                      label: getFleetOptions()?.find(
                        (item) => item?.value === fleet
                      )?.label,
                    };
                  })
                }
                onChange={(value) => {
                  let allValues = value?.map((item) => item.value);
                  if (allValues?.includes("clear")) {
                    // console.log(allValues)
                    setSelectedFleets([]);
                    // setSelectedHubs([]);
                    return;
                  }
                  if (
                    !selectedFleets?.includes("all") &&
                    allValues.includes("all")
                  ) {
                    setSelectedFleets(["all"]);
                    return;
                  }

                  if (selectedFleets.includes("all")) {
                    // console.log("first");
                    // console.log(getFleetOptions())
                    value = value.filter((item) => item.value !== "all");
                  }

                  setSelectedFleets(value.map((fleet) => fleet.value));
                }}
                name="fleets"
                isMulti={true}
              />
            </label>

            {/* <label className="w-full py-3">
              Select Makes
              <ReactSelect
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "transparent",
                    minWidth: "100%",
                    "&:hover": {
                      borderColor: "rgb(34 197 94)",
                    },
                  }),
                }}
                className="shadow-md rounded-md py-1 mb-2"
                options={getMakeOptions()}
                value={
                  selectedMakes &&
                  selectedMakes?.map((make) => {
                    return {
                      value: make,
                      label: getMakeOptions()?.find(
                        (item) => item?.value === make
                      )?.label,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);
                  if (allValues?.includes("clear")) {
                    setSelectedMakes([]);
                    setSelectedModels([]);
                    setSelectedConfigurations([]);
                    return;
                  }
                  if (
                    !selectedMakes?.includes("all") &&
                    allValues.includes("all")
                  ) {
                    setSelectedMakes(["all"]);
                    return;
                  }
                  if (selectedMakes.includes("all")) {
                    value = value.filter((item) => item.value !== "all");
                  }

                  let selectedMakesVal = allMakes?.data?.filter((make) =>
                    value?.map((item) => item.value)?.includes(make._id)
                  );

                  let selectedModelsVal = allModels?.data?.filter((model) =>
                    selectedModels?.map((item) => item)?.includes(model._id)
                  );

                  setSelectedConfigurations(
                    selectedConfigurations
                      ?.filter((config) =>
                        value?.find((make) => make === config.make?._id)
                      )
                      .map((configuration) => configuration)
                  );

                  setSelectedModels(
                    selectedModelsVal
                      ?.filter((model) =>
                        selectedMakesVal?.find(
                          (make) => make.makeName === model.makeName
                        )
                      )
                      .map((model) => model._id)
                  );

                  setSelectedMakes(value.map((make) => make.value));
                }}
                name="makes"
                isMulti={true}
              />
            </label>

            <label className="w-full py-3">
              Select Models
              <ReactSelect
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "transparent",
                    minWidth: "100%",
                    "&:hover": {
                      borderColor: "rgb(34 197 94)",
                    },
                  }),
                }}
                className="shadow-md rounded-md py-1 mb-2"
                options={getModelOptions()}
                value={
                  selectedModels &&
                  selectedModels?.map((model) => {
                    return {
                      value: model,
                      label: getModelOptions()?.find(
                        (item) => item.value === model
                      )?.label,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);
                  if (allValues?.includes("clear")) {
                    setSelectedModels([]);
                    setSelectedConfigurations([]);
                    return;
                  }
                  if (
                    !selectedModels?.includes("all") &&
                    allValues.includes("all")
                  ) {
                    setSelectedModels(["all"]);
                    return;
                  }
                  if (selectedModels.includes("all")) {
                    value = value.filter((item) => item.value !== "all");
                  }
                  // console.log(allValues)
                  // console.log({allMakes, allModels})

                  let selectedModelsVal = allModels?.data?.filter((model) =>
                    selectedModels?.map((item) => item)?.includes(model._id)
                  );

                  let selectedConfigurationsVal =
                    allConfigurations?.data?.filter((configurations) =>
                      selectedConfigurations
                        ?.map((item) => item)
                        ?.includes(configurations._id)
                    );

                  setSelectedConfigurations(
                    selectedConfigurationsVal
                      ?.filter((configuration) =>
                        selectedModelsVal?.find(
                          (model) => model.name === configuration.name
                        )
                      )
                      .map((configuration) => configuration._id)
                  );
                  setSelectedModels(value.map((model) => model.value));
                }}
                name="models"
                isMulti={true}
              />
            </label> */}

            <label className="w-full py-3">
              Select Configuration
              <ReactSelect
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "transparent",
                    minWidth: "100%",
                    "&:hover": {
                      borderColor: "rgb(34 197 94)",
                    },
                  }),
                }}
                className="shadow-md rounded-md py-1 mb-2"
                options={getConfigurationOptions()}
                value={
                  selectedConfigurations &&
                  selectedConfigurations?.map((configuration) => {
                    // console.log({selectedConfigurations})
                    // console.log( allConfigurations?.data?.find((item) => item._id === model))
                    return {
                      value: configuration,
                      label: getConfigurationOptions()?.find(
                        (item) => item.value === configuration
                      )?.label,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);
                  // console.log({allMakes, allModels})
                  if (allValues.includes("clear")) {
                    setSelectedConfigurations([]);
                    return;
                  }
                  if (
                    !selectedConfigurations?.includes("all") &&
                    allValues.includes("all")
                  ) {
                    setSelectedConfigurations(["all"]);
                    return;
                  }
                  if (selectedConfigurations.includes("all")) {
                    value = value.filter((item) => item.value !== "all");
                  }

                  setSelectedConfigurations(
                    value.map((configuration) => configuration.value)
                  );
                }}
                name="configurations"
                isMulti={true}
              />
            </label>

            <label className="w-full py-3">
              Contract Type
              <Select
                name="contractType"
                value={contractType}
                onChange={(e) => setContractType(e.target.value)}
              >
                <option value={""}>All</option>
                <option value={"CPKM"}>CPKM</option>
                <option value={"Service Contract"}>Non-CPKM</option>
                <option value={"De-enrolled"}>De-enrolled</option>
              </Select>
            </label>
            <label className="w-full py-3">
              Agreement
              <Select
                name="agreement"
                value={agreement}
                onChange={(e) => setAgreement(e.target.value)}
              >
                <option value={""}>All</option>
                <option value={"Yes"}>Yes</option>
                <option value={"No"}>No</option>
              </Select>
            </label>
          </div>
        </div>
        <div className="w-full  px-8 flex gap-6 items-center flex-wrap lg:flex-nowrap">
          <div className="w-3/4 py-3">
            <Input
              type="search"
              placeholder="Search Here"
              onChange={(e) => {
                setSearch(e.target.value?.trim());
              }}
            />
          </div>
          <Button
            type="button"
            disabled={!selectedFleets.length && !search}
            bgPrimary
            onClick={() => {
              clickSearchBtn();
              setPage(1);
              getData();
            }}
          >
            Search
          </Button>
          {downloading
            ? "Downloading..."
            : selectedFleets.length !== 0 && (
                <BiCloudDownload
                  className="w-10 h-10 text-green-500 hover:scale-105 cursor-pointer text-center"
                  onClick={async () => {
                    await getDownloadData();
                  }}
                />
              )}
          <Button
            className="!px-8 disabled:bg-gray-400"
            bgPrimary
            disabled={
              !checkPermission([permissions.manage_vehicles], currentUser)
            }
            onClick={() => setShowAddmodel(true)}
          >
            Add
          </Button>
          <Button
            className="!px-6 disabled:bg-gray-400 max-h-[40px] w-[200px]"
            bgPrimary
            disabled={
              !checkPermission([permissions.manage_vehicles], currentUser)
            }
            onClick={() => setShowBulkUploadModel(true)}
          >
            Bulk Upload
          </Button>
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {/* //comment */}
                      <TH scope="col">S.N.</TH>
                      <TH scope="col">Vehicle Number</TH>
                      <TH scope="col">Fitment Date</TH>
                      <TH scope="col">Last Inspection Date</TH>
                      <TH scope="col">Fleet</TH>
                      <TH scope="col">Hub</TH>
                      <TH scope="col">Make</TH>
                      <TH scope="col">Model</TH>
                      <TH scope="col">Year</TH>
                      <TH scope="col">Configuration</TH>
                      {/* <TH scope="col">Engine No</TH> */}
                      <TH scope="col">Contract Type</TH>
                      <TH scope="col">Mechanical Condition</TH>
                      <TH scope="col">Body Type</TH>
                      <TH scope="col">Application Type</TH>
                      {/* <TH scope="col">Vehicle Type</TH> */}
                      {/* <TH scope="col">Vehicle CC</TH>
                      <TH scope="col">GVW</TH> */}
                      <TH scope="col">Agreement</TH>
                      <TH scope="col">Actions</TH>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicles?.data &&
                      vehicles?.data.map((vehicle, index) => (
                        <ViewVehicleList
                          page={page}
                          vehicle={vehicle}
                          key={vehicle._id}
                          index={index}
                          getData={getData}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddmodel && (
        <AddVehicle handleClick={() => setShowAddmodel(false)}/>
      )}
      {showBulkUploadModel && (
        <BulkUpload handleClick={() => setShowBulkUploadModel(false)} />
      )}
      {vehicles !== undefined && vehicles.data && (
        <div className="container mx-auto flex justify-center items-center gap-2 py-5">
          <Button
            onClick={() => { trackPageClickEvent(ANALYTICS_EVENTS.CLICK_PREVIOUS_PAGE_BUTTON, "Vehicles", location.pathname); setPage((p) => Math.max(p - 1, 1)); }}
            disabled={page === 1}
            className="disabled:pointer-events-none disabled:opacity-70 px-0 "
            primary
          >
            <div className="flex gap-1 items-center">
              <IoMdArrowBack />
              Prev Page
            </div>
          </Button>
          {/* <span className="text-gray-600 px-4">{page}</span> */}
          <span className="text-gray-600 px-4">{`showing: ${
            (page - 1) * 100 + 1
          } - ${Math.min(vehicles?.paginate?.totalData, page * 100)} of ${
            vehicles?.paginate.totalData
          }`}</span>

          {vehicles.paginate.totalData > page * 20 && (
            <Button
              onClick={() => {
                trackPageClickEvent(ANALYTICS_EVENTS.CLICK_NEXT_PAGE_BUTTON, "Vehicles", location.pathname);
                setPage((p) => Math.min(p + 1, vehicles?.paginate?.totalPage));
              }}
              disabled={page === vehicles?.paginate?.totalPage}
              className="disabled:pointer-events-none disabled:opacity-70 px-0 "
              primary
            >
              <div className="flex gap-1 items-center">
                Next Page
                <IoMdArrowForward />
              </div>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Vehicles;
